import React, { useCallback, useEffect, useState } from 'react';
import {
  CalendarDays, CheckCircle2, Clock3, IdCard, Loader2, ShieldCheck,
  UserRound, XCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useToast } from '../../hooks/use-toast';
import client from '../../lib/api';
import { accountHook } from '../../lib/domHooks';

const emptyForm = {
  first_name: '',
  last_name: '',
  tc_no: '',
  birth_date: '',
};

const KycVerification = () => {
  const { user, refresh } = useAuth();
  const settings = useSettings();
  const { toast } = useToast();
  // Otomatik modda gönderilen bilgi anında doğrulanır; kapalıyken başvuru
  // yönetici onayına düşer (bkz. backend services/kyc.py).
  const autoApprove = settings.kyc_auto_approve !== false;
  const [kyc, setKyc] = useState({
    kyc_verified: false,
    request: null,
    identity: emptyForm,
  });
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get('/kyc/status');
      setKyc(data);
      setForm({
        first_name: data.identity?.first_name || user?.first_name || '',
        last_name: data.identity?.last_name || user?.last_name || '',
        tc_no: data.identity?.tc_no || user?.tc_no || '',
        birth_date: data.identity?.birth_date || user?.birth_date || '',
      });
      if (data.kyc_verified && !user?.kyc_verified) await refresh();
    } catch (error) {
      toast({
        title: 'KYC bilgileri alınamadı',
        description: error.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setLoading(false);
    }
  }, [refresh, toast, user]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key, value) => setForm((current) => ({
    ...current,
    [key]: value,
  }));

  const submit = async (event) => {
    event.preventDefault();
    const values = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      tc_no: form.tc_no.trim(),
      birth_date: form.birth_date,
    };
    if (
      !values.first_name
      || !values.last_name
      || !/^\d{11}$/.test(values.tc_no)
      || !values.birth_date
    ) {
      toast({
        title: 'Bilgileri kontrol edin',
        description: 'Ad, soyad, 11 haneli TC kimlik no ve doğum tarihi zorunludur.',
      });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await client.post('/kyc/request', values);
      await Promise.all([load(), refresh()]);
      toast(data?.auto_approved === false ? {
        title: 'Başvurunuz alındı',
        description: 'Kimlik bilgileriniz yönetici onayına gönderildi. Onaylandığında bilgilendirileceksiniz.',
      } : {
        title: 'KYC doğrulamanız tamamlandı',
        description: 'Kimlik bilgileriniz doğrulandı. Para yatırma ve çekme işlemlerini kullanabilirsiniz.',
      });
    } catch (error) {
      toast({
        title: 'KYC başvurusu gönderilemedi',
        description: error.response?.data?.detail || 'Bilgilerinizi kontrol edip tekrar deneyin.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pusu-panel p-8 max-w-3xl flex items-center justify-center gap-2 text-gray-400" {...accountHook('loading')}>
        <Loader2 size={18} className="animate-spin text-[var(--pusu-gold)]" />
        KYC bilgileri yükleniyor
      </div>
    );
  }

  const verified = Boolean(kyc.kyc_verified || user?.kyc_verified);
  const request = kyc.request;
  // Kayıtlı TC ve doğum tarihini üye değiştiremez (sunucu da 409 döner);
  // düzeltme yalnızca panelden yapılır.
  const tcLocked = Boolean(kyc.identity?.tc_no);
  const birthLocked = Boolean(kyc.identity?.birth_date);

  return (
    <div className="space-y-4 max-w-3xl" data-bhs-status={verified ? 'verified' : 'unverified'}>
      <div className="pusu-panel p-6" {...accountHook('page-header')}>
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
              verified ? 'bg-green-500/10 text-green-400' : 'bg-[var(--pusu-gold)]/10 text-[var(--pusu-gold)]'
            }`}
          >
            {verified ? <CheckCircle2 size={24} /> : <ShieldCheck size={24} />}
          </div>
          <div>
            <h1 {...accountHook('title')} className={`text-2xl font-black ${verified ? 'text-green-400' : 'gold-text'}`}>
              {verified ? 'KYC Doğrulandı' : 'KYC Doğrulama'}
            </h1>
            <p className="text-sm text-gray-400 mt-1" {...accountHook('subtitle')}>
              {verified
                ? 'Kimlik doğrulamanız tamamlandı. Para yatırma ve çekme işlemlerini kullanabilirsiniz.'
                : autoApprove
                  ? 'Ad, soyad, TC kimlik numarası ve doğum tarihinizi girin; bilgileriniz anında doğrulanır.'
                  : 'Para çekebilmek için kimlik doğrulaması gerekir. Bilgilerinizi gönderin; yönetici onayından sonra hesabınız doğrulanır.'}
            </p>
          </div>
        </div>
      </div>

      {!verified && request && (
        <RequestStatus request={request} />
      )}

      {!verified && (
        <div className="pusu-panel p-6" {...accountHook('panel')}>
          <h2 className="font-bold gold-text mb-2" {...accountHook('title')}>Kimlik Bilgileri</h2>
          <p className="text-sm text-gray-400 mb-5">
            Tüm alanlar zorunludur. Bilgileriniz kimliğinizde yazdığı biçimde
            olmalıdır ve
            {autoApprove
              ? ' gönderdiğinizde anında doğrulanır.'
              : ' gönderdiğinizde yönetici onayına düşer.'}
          </p>

          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" {...accountHook('form')}>
            <Field name="first_name" icon={UserRound} label="Ad">
              <input
                required
                maxLength={128}
                className="pusu-input pl-10"
                value={form.first_name}
                onChange={(event) => update('first_name', event.target.value)}
                placeholder="Adınız"
                autoComplete="given-name"
              />
            </Field>
            <Field name="last_name" icon={UserRound} label="Soyad">
              <input
                required
                maxLength={128}
                className="pusu-input pl-10"
                value={form.last_name}
                onChange={(event) => update('last_name', event.target.value)}
                placeholder="Soyadınız"
                autoComplete="family-name"
              />
            </Field>
            <Field name="tc_no" icon={IdCard} label="TC Kimlik Numarası">
              <input
                required
                inputMode="numeric"
                pattern="\d{11}"
                minLength={11}
                maxLength={11}
                readOnly={tcLocked}
                className={`pusu-input pl-10${tcLocked ? ' opacity-60' : ''}`}
                value={form.tc_no}
                onChange={(event) => update(
                  'tc_no',
                  event.target.value.replace(/\D/g, '').slice(0, 11),
                )}
                placeholder="11 haneli TC kimlik no"
                autoComplete="off"
              />
            </Field>
            <Field name="birth_date" icon={CalendarDays} label="Doğum Tarihi">
              <input
                required
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                readOnly={birthLocked}
                className={`pusu-input pl-10${birthLocked ? ' opacity-60' : ''}`}
                value={form.birth_date}
                onChange={(event) => update('birth_date', event.target.value)}
              />
            </Field>
            {(tcLocked || birthLocked) && (
              <p className="sm:col-span-2 text-xs text-gray-400">
                Kayıtlı TC kimlik numarası ve doğum tarihi değiştirilemez.
                Hatalıysa canlı destekle iletişime geçin.
              </p>
            )}
            <button
              disabled={submitting}
              type="submit"
              {...accountHook('submit')}
              className="gold-btn sm:col-span-2 py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting
                ? <Loader2 size={16} className="animate-spin" />
                : <ShieldCheck size={16} />}
              {autoApprove ? 'Doğrula' : 'Onaya Gönder'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({
  name, icon: Icon, label, children,
}) => (
  <div {...accountHook('field', name)}>
    <label className="text-xs text-gray-400 mb-1.5 block">{label}</label>
    <div className="relative">
      <Icon
        size={16}
        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)] pointer-events-none"
      />
      {children}
    </div>
  </div>
);

const RequestStatus = ({ request }) => {
  const config = request.status === 'rejected'
    ? {
      icon: XCircle,
      title: 'KYC doğrulamanız geri alındı',
      text: request.admin_note || 'Bilgilerinizi kontrol edip yeniden gönderin.',
      className: 'text-red-400 border-red-500/20 bg-red-500/5',
    }
    : {
      icon: Clock3,
      title: 'KYC kaydınız incelemede',
      text: 'Bilgileriniz yönetici incelemesinde. Gerekirse aşağıdan güncelleyebilirsiniz.',
      className: 'text-amber-300 border-amber-400/20 bg-amber-400/5',
    };
  const Icon = config.icon;
  return (
    <div
      className={`pusu-panel p-4 border ${config.className}`}
      data-bhs-status={request.status}
      {...accountHook('notice')}
    >
      <div className="flex items-start gap-3">
        <Icon size={18} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">{config.title}</div>
          <div className="text-sm opacity-80 mt-1">{config.text}</div>
        </div>
      </div>
    </div>
  );
};

export default KycVerification;
